import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nationality_data } from "./all";
import { RiArrowRightDoubleLine } from "react-icons/ri";

import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

import { toEthiopian, toGregorian } from "ethiopian-date";
import "react-datepicker/dist/react-datepicker.css";
import DisplayProfileCard from "./components/displayCard";
import EditCard from "./components/editCard";
import UploadDocument from "./components/uploadDocument";
import DocumentPreview from "./components/documentPreview";
import NavBar from "../../Components/Nav/newNavBar";
import { useFormik } from "formik";
import { updateFormSchema } from "./formSchema";
import useUpdateDemoLayoutForm from "./updateDemoLayoutForm";
import { submitUpdateThunk } from "../../Redux/thunks/updateThunks";
import ConfirmModal from "./components/confirmModal";
import NotificationModal from "../../Components/NotificationModal";
import ContactOtpModal from "../../Components/ContactOtpModal/ContactOtpModal";
import { buildContactOtpSteps } from "../../utils/contactOtp";
import { deepEqual } from "../../hooks/deepCheck";
import Footer from "../../Components/Footer/footer";
import { jsPDF } from 'jspdf';
import { useNavigate } from "react-router-dom";


const UpdateDemo = () => {
    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}/${month}/${day}`;
    };

    const formattedEthiopianDate = (gregorianDate) => {
        const formattedDate = gregorianDate?.replace(/\//g, "-") || "";
        const [year, month, day] = formattedDate?.split("-").map(Number);
        const ethiopian = toEthiopian(year, month, day);
        const formattedEthiopianDate = `${String(ethiopian[0])?.padStart(
            2,
            "0"
        )}/${String(ethiopian[1])?.padStart(2, "0")}/${String(
            ethiopian[2]
        )?.padStart(2, "0")}`;
        return formattedEthiopianDate;
    };

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { data, isLoading, error } = useSelector((state) => state.update);
    const [openNotificationModal, setOpenNotificationModal] = useState(false);
    const [userError, setUserError] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [contactOtpOpen, setContactOtpOpen] = useState(false);
    const [contactOtpSteps, setContactOtpSteps] = useState([]);
    const [pendingFormData, setPendingFormData] = useState(null);
    const sectionRefs = useRef({});
    const [highlightField, setHighlightField] = useState(null);
    const uniqueIdObject = useSelector((state) => state.otp.data);
    const [rateLimiterCount, setRateLimiterCount] = useState(0);
    const fileInputRef = useRef(null);
    const toastId = useRef(null);

    useEffect(() => {
        if (error || data) {
            setOpenNotificationModal(true);
            setOpenModal(false);
            setContactOtpOpen(false);
            setPendingFormData(null);
        }
    }, [error, data]);
    useEffect(() => {
        setOpenNotificationModal(false);
        setUserError("");
        setOpenModal(false);
    }, []);

    const initialState = {
        fullnameEng: uniqueIdObject?.fullName?.eng || "",
        fullnameAmh: uniqueIdObject?.fullName?.amh || "",
        gender: uniqueIdObject?.gender?.eng || "",
        region: uniqueIdObject?.region?.eng || "",
        zone: uniqueIdObject?.zone?.eng || "",
        woreda: uniqueIdObject?.woreda?.eng || "",
        nationality:
            nationality_data?.find(
                (obj) => obj.name === uniqueIdObject?.residenceStatus?.eng
            )?.id || "57",
        dateOfBirth: formatDate(uniqueIdObject?.dateOfBirth),
        phone: uniqueIdObject?.phone?.replace(/^[+]/, '').trim() || "",
        email: uniqueIdObject?.email || "",
        uin: uniqueIdObject?.uin || "",
        signature: uniqueIdObject?.signature || "",
        proofOfAddress: uniqueIdObject?.proofOfAddress || "",
        individualBiometrics: uniqueIdObject?.individualBiometrics || "",
        proofOfIdentity: uniqueIdObject?.proofOfIdentity || "",
        idschemaVersion: uniqueIdObject?.idschemaVersion || "",
        documentOne:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "proofOfAddress"
            ) || "",
        documentTwo:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "proofOfIdentity"
            ) || "",
        documentThree:
            uniqueIdObject?.documents?.find(
                (obj) => obj?.category === "CONS"
            ) || "",
        photo: uniqueIdObject?.photo || "",
        poaUploadProof: "",
        poiUploadProof: "",
        dobAmh: formattedEthiopianDate(uniqueIdObject?.dateOfBirth),
        uploadPoaDocumentType: "",
        uploadPoiDocumentType: "",
    };

    const doSubmit = (data) => {
        const changedFields = Object.keys(initialState).filter(
            (key) => initialState[key] !== data[key]
        );

        const isImmediateUpdate =
            changedFields.length > 0 &&
            changedFields.every((field) => field === "phone" || field === "email");

        const phoneChanged = initialState.phone !== data.phone;
        const emailChanged = initialState.email !== data.email;

        const updatedDTO = {
            poaDocumentType: data.uploadPoaDocumentType,
            poiDocumentType: data.uploadPoiDocumentType,
            fullName: {
                eng: data.fullnameEng?.trim(),
                amh: data.fullnameAmh?.trim(),
            },
            gender: {
                eng: data.gender === "Male" ? "Male" : "Female",
                amh: data.gender === "Male" ? "ወንድ" : "ሴት",
            },
            dateOfBirth: data.dateOfBirth,
            residenceStatus: {
                eng: nationality_data?.find(
                    (item) => item.id === Number(data.nationality)
                ).name,
                amh: nationality_data?.find(
                    (item) => item.id === Number(data.nationality)
                ).nameamh,
            },
            region: {
                eng: uniqueIdObject?.region?.eng,
                amh: uniqueIdObject?.region?.amh,
            },
            zone: {
                eng: uniqueIdObject?.zone?.eng,
                amh: uniqueIdObject?.zone?.amh,
            },
            woreda: {
                eng: uniqueIdObject?.woreda?.eng,
                amh: uniqueIdObject?.woreda?.amh,
            },
            phone: data?.phone?.replace(/^[+]/, '').trim() || "",
            email: data.email,
            uin: data.uin,
            signature: data.signature,
            proofOfAddress: {
                format: "PDF",
                type: "DOC005",
                value: "proofOfAddress",
            },
            individualBiometrics: {
                format: "cbeff",
                version: 1,
                value: "individualBiometrics_bio_CBEFF",
            },
            proofOfIdentity: {
                format: "PDF",
                type: "DOC005",
                value: "proofOfIdentity",
            },
            idschemaVersion: 1,
            documentOne: values.documentOne,
            documentTwo: values.documentTwo,
            documentThree: data.documentThree,
            photo: data.photo,
            isImmediateUpdate,
            ...(phoneChanged && { phoneOtpVerified: true }),
            ...(emailChanged && { emailOtpVerified: true }),
        };

        dispatch(
            submitUpdateThunk(
                updatedDTO,
                uniqueIdObject?.uin,
                "FIN",
                data.signature,
                uniqueIdObject.regId
            )
        );
    };

    const submitter = async (data) => {
        if (rateLimiterCount <= 3) {
            setRateLimiterCount((prevCount) => prevCount + 1);
        }
        setUserError("");

        if (deepEqual(initialState, data)) {
            setUserError("You have not made any changes.");
            setOpenNotificationModal(true);
            setOpenModal(false);
            return;
        }

        const steps = buildContactOtpSteps(initialState, data);
        if (steps.length > 0) {
            setPendingFormData(data);
            setContactOtpSteps(steps);
            setContactOtpOpen(true);
            setOpenModal(false);
            return;
        }

        doSubmit(data);
    };

    const handleContactOtpVerified = () => {
        if (pendingFormData) {
            doSubmit(pendingFormData);
        }
    };

    if (rateLimiterCount >= 3) {
        setTimeout(() => {
            setRateLimiterCount(0);
        }, 10000);
    }
    const {
        values,
        touched,
        errors,
        dirty,
        setFieldValue,
        handleChange,
        handleSubmit,
        handleBlur,
    } = useFormik({
        initialValues: initialState,
        enableReinitialize: true,
        validationSchema: updateFormSchema(initialState),
        onSubmit: submitter,
    });

    useEffect(() => {
        const processFile = (file, targetField) => {
            if (!file) return;

            const setPDFBase64 = (blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.replace(/^data:.*?base64,/, "");
                    setFieldValue(`${targetField}.value`, base64String);
                };
                reader.readAsDataURL(blob);
            };

            if (file.type.startsWith("image/")) {
                const imageReader = new FileReader();
                imageReader.onloadend = () => {
                    const img = new Image();
                    img.onload = () => {
                        const pdf = new jsPDF();
                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight = pdf.internal.pageSize.getHeight();

                        const imgWidth = img.width;
                        const imgHeight = img.height;

                        const scale = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
                        const scaledWidth = imgWidth * scale;
                        const scaledHeight = imgHeight * scale;

                        const x = (pdfWidth - scaledWidth) / 2;
                        const y = (pdfHeight - scaledHeight) / 2;

                        pdf.addImage(img, "JPEG", x, y, scaledWidth, scaledHeight);

                        const pdfBlob = pdf.output("blob");
                        setPDFBase64(pdfBlob);
                    };
                    img.src = imageReader.result;
                };
                imageReader.readAsDataURL(file);
            } else {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result.replace(/^data:.*?base64,/, "");
                    setFieldValue(`${targetField}.value`, base64String);
                };
                reader.readAsDataURL(file);
            }
        };

        if (values?.poaUploadProof) {
            processFile(values.poaUploadProof, "documentOne");
        }

        if (values?.poiUploadProof) {
            processFile(values.poiUploadProof, "documentTwo");
        }

      }, [values?.poaUploadProof, values?.poiUploadProof, setFieldValue]);

    const { layout } = useUpdateDemoLayoutForm({
        values,
        identity: uniqueIdObject,
    });

    const handleFieldBlur = (field) => {
        const identityFields = ["name", "nationality", "dateOfBirth", "dobAmh"];
        const locationFields = ["region", "zone", "woreda"];

        let proofField = null;

        const identityChanged =
            initialState?.fullnameEng !== values.fullnameEng ||
            initialState?.fullnameAmh !== values.fullnameAmh ||
            initialState?.nationality !== values.nationality ||
            initialState?.dateOfBirth !== values.dateOfBirth;

        const locationChanged =
            initialState?.region !== values.region ||
            initialState?.zone !== values.zone ||
            initialState?.woreda !== values.woreda;

        if (identityFields.includes(field) && identityChanged) {
            proofField = "poiUploadProof";
        }

        if (locationFields.includes(field) && locationChanged) {
            proofField = "poaUploadProof";
        }

        if (proofField) {
            setTimeout(() => {
                const section = sectionRefs.current[proofField];

                if (section) {
                    section.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 0);
            setHighlightField(proofField);

            setTimeout(() => {
                setHighlightField(null);
            }, 2500);
        }
    };
    
    return (
        <div className='relative bg-[whitesmoke] z-0 flex flex-col items-center justify-center w-full min-h-screen bg-bottom bg-cover bg-opacity-6 font-lato'>
            <NavBar />
            <div className='max-w-[1400px] mx-auto px-5 mt-20 md:mt-40 mb-5'>
                <p className='w-full text-MetallicBlue font-semibold text-3xl'>
                    {t("update-demo")}
                </p>
            </div>
            <form
                onSubmit={handleSubmit}
                className='max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-5 text-MetallicBlue px-5 mb-20'
            >
                {layout.map((layoutItem, index) =>
                    layoutItem.type === "document" ? (
                        <UploadDocument
                            key={index}
                            layout={layoutItem}
                            values={values}
                            errors={errors}
                            fields={layoutItem.formFields}
                            handleBlur={handleBlur}
                            touched={touched}
                            setFieldValue={setFieldValue}
                            fileInputRef={fileInputRef}
                            initialState={initialState}
                            sectionRefs={sectionRefs}
                            highlightField={highlightField}
                        />
                    ) : layoutItem.type === "edit" ? (
                        <EditCard
                            key={index}
                            changeHandler={handleChange}
                            setFieldValue={setFieldValue}
                            values={values}
                            errors={errors}
                            title={layoutItem.title}
                            showEdit={layoutItem.showEdit}
                            fields={layoutItem.formFields}
                            handleBlur={handleBlur}
                            touched={touched}
                            handleFieldBlur={handleFieldBlur}
                        />
                    ) : layoutItem.type === "preview" ? (
                        <DocumentPreview
                            key={index}
                            title={layoutItem.title}
                            data={[
                                {
                                    doc: values?.documentOne?.value,
                                    title: "Proof of Address",
                                },
                                {
                                    doc: values?.documentTwo?.value,
                                    title: "Proof of Identity",
                                },
                                {
                                    doc: values?.documentThree?.value,
                                    title: "Consent Form",
                                },
                            ]}
                        />
                    ) : layoutItem.type === "" ? (
                        <div key={index} className='col-span-1'></div>
                    ) : (
                        <DisplayProfileCard
                            key={index}
                            name={uniqueIdObject?.fullName || ""}
                            fin={initialState.uin}
                            photo={initialState.photo}
                            verified={true}
                        />
                    )
                )}
                <div className='order-first md:col-span-3 py-5 px-7 flex flex-col w-full gap-3 bg-gray-200 leading-5'>
                    <p className='font-bold text-2xl'>{t("instructions")}</p>
                    <p>{t("update-instructions-description1")}</p>
                    <p>{t("update-instructions-description2")}</p>
                    <p>{t("update-instructions-description3")}</p>
                    <p>{t("update-instructions-description4")}</p>
                    <p>{t("update-instructions-description5")}</p>
                    <p>{t("instructions-privacy-notice")}</p>
                </div>
                <div className='w-full flex justify-center items-center bg-gray-200 shadow-sm py-10'>
                    <button
                        type='button'
                        onClick={() => setOpenModal(!openModal)}
                        className={`${!dirty ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} bg-MetallicBlue items-center flex justify-center gap-3 text-white py-4 px-8 rounded-full text-xl`}
                        disabled={!dirty || isLoading}
                    >
                        <p>{t("submit-changes")}</p>
                        <RiArrowRightDoubleLine size={30} />
                    </button>
                </div>
                <ConfirmModal
                    open={openModal}
                    handleClose={() => setOpenModal(false)}
                    submit={handleSubmit}
                    loading={isLoading}
                />
                <ContactOtpModal
                    open={contactOtpOpen}
                    steps={contactOtpSteps}
                    onVerified={handleContactOtpVerified}
                    submitting={isLoading}
                    onClose={() => {
                        if (isLoading) return;
                        setContactOtpOpen(false);
                        setPendingFormData(null);
                    }}
                />
                {!isLoading ? (
                    <NotificationModal
                        open={openNotificationModal}
                        error={error || userError ? true : false}
                        title={error || userError ? "Error" : "Success"}
                        message={
                            userError
                                ? userError
                                : error
                                ? error
                                : data?.response === "OK"
                                ? data?.response
                                : null
                        }
                        handleClose={() => {
                            setOpenNotificationModal(false);
                            navigate("/dashboard");
                        }}
                    />
                ) : null}
            </form>
            <Footer />
        </div>
    );
};

export default UpdateDemo;